import {
  Box, Typography, Stack, Tooltip as MuiTooltip,
} from '@mui/material';
import { Link as RouterLink } from 'react-router-dom';
import { styled } from '@mui/material/styles';
import { TrendingDown, TrendingUp, TrendingFlat } from '@mui/icons-material';
import { formatDistance, isBefore, isEqual } from 'date-fns';
import { useDispatch } from 'react-redux';
import { Chart } from 'react-chartjs-2';
import { useLayoutEffect, useState } from 'react';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  LineElement,
  PointElement,
  ArcElement,
  Title,
  Tooltip,
  Legend,
  Filler,
} from 'chart.js';
import { ConfirmDialogBox } from '../../../components/CustomDialogBox';
import { MutedText } from '../../../components/CustomTypography';
import { identifyAndConvertDate } from '../../../utils/formatTime';
import { DATE_FORMAT } from '../../../utils/constants';
import { explainHealthScore } from '../../../features/HealthScore/HealthScoreAction';
import { HealthScoreExplain } from '../../../modules/HealthScore';
import useToggle from '../../../hooks/useToggle';
import { CustomDataGrid } from '../../../components/CustomDataGrid';
import styles from './style';
import useStyles from '../../../hooks/useStyles';
import { getAccountListColumns } from './accountsConstants';
import { getDate, getMomentDate, isAfter90Days } from '../../../utils/getDate';
import { CustomCircularProgress } from '../../../components/CustomCircularProgress';

const MainContainerStyle = styled(Box)(({ theme }) => ({
  width: '100%',
  '& div[aria-rowindex]': {
    borderBottom: '1px solid #eee',
  },
  '& > div': {
    backgroundColor: theme.palette.primary.contrastText,
  },
}));

const TypographyStyle = styled(Typography)(({ theme, packageEnabled }) => ({
  fontSize: 14,
  color: packageEnabled ? theme.palette.secondary.mainColor : 'grey',
  cursor: packageEnabled ? 'pointer' : 'default',
  textDecoration: 'underline',
}));

const normalizedValue = (value) => (value !== null ? Math.min(Math.max(value, 0), 100) : 0);

const isPackageDisabled = (params) => {
  const { packageEnabled, isEnabled } = params.row;
  if (packageEnabled === false || isEnabled === false) return true;
  return false;
};

export default function AccountDetailsTable({
  AccountTableData, permissions, siteConfiguration, selectedFilters, accountListMenuId, isLicense = true,
}) {
  const dispatch = useDispatch();
  const [open, setOpen] = useToggle();
  const [openSeatsInfo, setOpenSeatsInfo] = useToggle();
  const [selectedHealthScore, setSelectedHealthScore] = useState(null);

  const handleClickScore = (accountId, score, healthScoreId) => {
    setOpen();
    setSelectedHealthScore(score);
    if (accountId && healthScoreId) {
      dispatch(explainHealthScore({ accountId, healthScoreId }));
    } else if (accountId) {
      dispatch(explainHealthScore({ accountId }));
    }
  };

  const columnInfo = getAccountListColumns(siteConfiguration?.scoreTrendTimeframe, setOpenSeatsInfo);
  columnInfo.forEach((item) => {
    if (item.field === 'accountName') {
      item.renderCell = (params) => <AccountNameCell isLicense={isLicense} params={params} selectedFilters={selectedFilters} accountListMenuId={accountListMenuId} />;
    } else if (item.field === 'status') {
      item.renderCell = (params) => <AccountStatusCell params={params} />;
    } else if (item.field === 'renewalDate') {
      item.renderCell = (params) => <RenewalDateCell params={params} />;
    } else if (item.field === 'seatsAssigned') {
      item.renderCell = (params) => <SeatsCell params={params} />;
    } else if (item.field === 'healthScore') {
      item.renderCell = (params) => <HealthScore isAccountHealthScore params={params} onClick={() => handleClickScore(params.row.account_id, normalizedValue(params.row.healthScore), params.row.healthScoreId)} />;
    } else if (item.field === 'healthScoreTrendThumbnail') {
      item.renderCell = (params) => <HealthScoreTrendThumbnail params={params} view={siteConfiguration?.scoreTrendTimeframe ?? 'monthly'} trendsData={siteConfiguration?.scoreTrendTimeframe === 'weekly' ? (params?.row?.weeklyTrendsData || []) : (params?.row?.trendsData || [])} />;
    } else if (item.field === 'trendValue') {
      item.renderCell = (params) => <AccountSignalCell params={params} healthScoreTrend={siteConfiguration?.scoreTrendTimeframe === 'weekly' ? (params?.row?.weeklyTrend) : (params?.row?.healthScoreTrend)} trendValue={siteConfiguration?.scoreTrendTimeframe === 'weekly' ? (params?.row?.weeklyTrendValue) : (params?.row?.trendValue)} />;
    } else if (item.field === 'weeklyTrendValue') {
      item.renderCell = (params) => <AccountSignalCell params={params} healthScoreTrend={siteConfiguration?.scoreTrendTimeframe === 'weekly' ? (params?.row?.weeklyTrend) : (params?.row?.healthScoreTrend)} trendValue={siteConfiguration?.scoreTrendTimeframe === 'weekly' ? (params?.row?.weeklyTrendValue) : (params?.row?.trendValue)} />;
    }
    return false;
  });

  const initialState = {
    sorting: {
      sortModel: [{ field: 'seatsAssigned', sort: 'desc' }],
    },
  };

  return (
    <MainContainerStyle>
      <CustomDataGrid
        rowId={isLicense ? 'licenseId' : 'account_id'}
        initialState={initialState}
        columns={columnInfo}
        rows={AccountTableData || []}
        rowHeight={70}
        rowsPerPageOptions={[20]}
        pageSize={20}
        loading={!AccountTableData}
      />
      <HealthScoreExplain open={open} setOpen={setOpen} permissions={permissions} healthScore={selectedHealthScore} />

      <ConfirmDialogBox
        openDialog={openSeatsInfo}
        title="Account Seats"
        message="Maximum Seats of all licenses are visible to indicate Account Capacity. For multi-org Accounts, Max. Seats are summed up across organizations to indicate total capacity."
        onClose={setOpenSeatsInfo}
        cancelText="OK"
      />
    </MainContainerStyle>
  );
}

const trendIcon = (trendType, trendValue) => {
  if (trendType === 'rising') {
    return (
      <MuiTooltip title="Trending">
        <Typography sx={{
          display: 'flex', flexDirection: 'row', color: 'green', alignItems: 'center',
        }}
        >
          <TrendingUp />
          {trendValue
            ? (
              <Typography sx={{ fontSize: 13 }}>
                {` +${trendValue}`}
              </Typography>
            )
            : null}
        </Typography>
      </MuiTooltip>
    );
  }
  if (trendType === 'falling') {
    return (
      <MuiTooltip title="Declining">
        <Typography sx={{
          display: 'flex', flexDirection: 'row', color: 'red', alignItems: 'center',
        }}
        >
          <TrendingDown />
          {trendValue
            ? (
              <Typography sx={{ fontSize: 13 }}>
                {` ${trendValue}`}
              </Typography>
            )
            : null}
        </Typography>
      </MuiTooltip>
    );
  }
  if (trendType === 'stable') {
    return (
      <MuiTooltip title="Stable">
        <Typography sx={{
          display: 'flex', flexDirection: 'row', color: 'gray', alignItems: 'center',
        }}
        >
          <TrendingFlat />
        </Typography>
      </MuiTooltip>
    );
  }
  return null;
};

export function AccountSignalCell({ params, healthScoreTrend, trendValue }) {
  if (isPackageDisabled(params)) return '- - -';

  return (
    <Stack direction="row" alignItems="center" justifyContent="space-between">
      {healthScoreTrend
        ? (
          <Typography>
            {trendIcon(healthScoreTrend, trendValue)}
          </Typography>
        )
        : null }

    </Stack>
  );
}

export function AccountNameCell({
  isLicense, params, selectedFilters, accountListMenuId,
}) {
  const classes = useStyles(styles)();

  const name = params?.value || params?.row?.[isLicense ? 'orgId' : 'account_id'];
  const packageEnabled = params?.row?.packageEnabled;
  const packageCount = params?.row?.packages?.length ?? 0;
  const packageLabel = params?.row?.packageLabel || params?.row?.packageId;
  const isNew = params?.row?.installDate && isAfter90Days(params.row.installDate);
  const isExcluded = !params?.row?.visibility;

  const searchString = isLicense ? `?orgId=${params.row.orgId}&licenseId=${params.row.licenseId}` : `?accountId=${params.row.account_id}`;
  const navigateTo = {
    pathname: '/account-page',
    search: searchString,
  };

  const state = {
    accountName: params.value,
    selectedFilters,
    accountListMenuId,
  };

  return (
    <Stack maxWidth="100%">
      <Stack direction="row" alignItems="center" spacing={1}>
        <MuiTooltip title={name} placement="top" arrow>
          <TypographyStyle
            packageEnabled={!isLicense || packageEnabled}
            component={(!isLicense || packageEnabled) && RouterLink}
            to={navigateTo}
            state={state}
            sx={{
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
              flexShrink: 1,
              minWidth: 0,
            }}
          >
            {name}
          </TypographyStyle>
        </MuiTooltip>

        {isLicense && (packageEnabled ? (
          <>
            {isNew && <Typography className={classes.cellSubNewTextStyle}>New</Typography>}
            {isExcluded && <Typography className={classes.cellSubTextStyle}>{params.row.visibility ? null : ' (Excluded)'}</Typography>}
          </>
        ) : (
          <Typography className={classes.cellSubTextStyle}>(Disabled)</Typography>
        ))}
      </Stack>

      {!isLicense && (
      <MutedText
        variant="caption"
        text={`${packageCount} Packages`}
        sxProps={{
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          whiteSpace: 'nowrap',
          flexShrink: 1,
          minWidth: 0,
        }}
      />
      )}

      {isLicense && (
      <MutedText
        variant="caption"
        text={packageLabel}
        sxProps={{
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          whiteSpace: 'nowrap',
          flexShrink: 1,
          minWidth: 0,
        }}
      />
      )}
    </Stack>
  );
}

function AccountStatusCell({ params }) {
  const classes = useStyles(styles)();
  const status = params.row.status !== 'Uninstalled' ? params.row.status : 'Churned';
  const createdDate = identifyAndConvertDate(params.row.activationDate ? params.row.activationDate.slice(0, 10) : null, DATE_FORMAT.DAY_DATE_GRAPH, 'YYYY-MM-DD');
  return (
    <Box className={classes.cellBoxStyle}>
      <Typography className={classes.cellTextStyle}>{status}</Typography>
      {status === 'Active' && <Typography className={classes.cellSubTextStyle}>{`From ${createdDate}`}</Typography>}
    </Box>
  );
}

export function RenewalDateCell({ params }) {
  const classes = useStyles(styles)();
  const renewalDate = identifyAndConvertDate(params.row.renewalDate ? params.row.renewalDate.slice(0, 10) : null, DATE_FORMAT.DAY_DATE_GRAPH, 'YYYY-MM-DD');
  const todayDate = getDate();
  const renewDate = getDate(renewalDate);
  let renewalStatus = '';
  if (isEqual(todayDate, renewDate)) {
    renewalStatus = 'today';
  } else if (isBefore(todayDate, renewDate)) {
    renewalStatus = formatDistance(renewDate, todayDate, { addSuffix: true });
  } else {
    renewalStatus = renewalDate ? 'expired' : 'NA';
  }
  return (
    <Box className={classes.cellBoxStyle}>
      {params.row.renewalDate === null
        ? <Typography className={classes.cellTextStyle}>Does Not Expire</Typography>
        : (
          <>
            <Typography className={classes.cellTextStyle}>{renewalDate}</Typography>
            <Typography className={classes.cellSubTextStyle}>{renewalStatus}</Typography>
          </>
        )}
    </Box>
  );
}

export function SeatsCell({ params }) {
  const classes = useStyles(styles)();
  if (params?.row?.totalSeats && (parseInt(params?.row?.totalSeats, 10)) > 0) {
    const assignedSeatsPercent = parseFloat(params.row.assignedSeatsPercent).toFixed(0);
    return (
      <Box className={classes.cellBoxStyle}>
        <Typography className={classes.cellTextStyle}>{params.row.totalSeats}</Typography>
        {assignedSeatsPercent !== 'NaN' ? (
          <Typography className={classes.cellSubTextStyle}>{`${(assignedSeatsPercent > 100 ? '100% assigned*' : `${assignedSeatsPercent}% assigned`)}`}</Typography>
        ) : null}
      </Box>
    );
  } return (
    <Stack spacing={0.5}>
      <Typography className={classes.cellSubTextStyle}>Site-Wide License</Typography>
    </Stack>
  );
}

export function HealthScore({ params, onClick, isAccountHealthScore = false }) {
  if (isPackageDisabled(params)) return '- - -';
  const { healthScore } = params.row;

  return (
    <Box onClick={onClick} sx={{ cursor: isAccountHealthScore ? 'pointer' : 'auto' }}>
      <CustomCircularProgress value={normalizedValue(healthScore)} />
    </Box>
  );
}

export function HealthScoreTrendThumbnail({ params, trendsData = [], view = 'monthly' }) {
  if (isPackageDisabled(params)) return '- - -';

  const chartData = {
    labels: trendsData.map((data) => data.startDate),
    datasets: [
      {
        type: 'line',
        label: 'KPI Score',
        data: trendsData.map((data) => data.data),
        fill: false,
        borderWidth: 1,
        borderColor: '#4482FF',
        pointStyle: 'circle', // Customize the point style
        pointBackgroundColor: '#4482FF', // Customize the point color
        pointRadius: 1, // Customize the point radius
      },
      {
        type: 'line',
        label: 'KPI Scores',
        data: trendsData.map((items) => items.avgData),
        fill: false,
        borderWidth: 1,
        borderColor: '#f28e00',
        pointStyle: 'circle', // Customize the point style
        pointBackgroundColor: '#f28e00', // Customize the point color
        pointRadius: 1, // Customize the point radius
      },
      {
        type: 'bar',
        label: 'KPI Score (Bar)',
        data: trendsData.map((data) => data.data),
        backgroundColor: '#4482FF',
        borderColor: '#4482FF',
        borderWidth: 1,
      },
    ],
  };

  const options = {
    plugins: {
      legend: {
        display: false,
      },
      tooltip: {
        enabled: false,
        mode: 'index',
        external(context) {
          let tooltipEl = document.getElementById('chartjs-tooltip');
          if (!tooltipEl) {
            tooltipEl = document.createElement('div');
            tooltipEl.id = 'chartjs-tooltip';
            tooltipEl.innerHTML = '<table></table>';
            document.body.appendChild(tooltipEl);
          }

          const tooltipModel = context.tooltip;
          if (tooltipModel.opacity === 0) {
            tooltipEl.style.opacity = '0';
            return;
          }

          tooltipEl.classList.remove('below', 'no-transform');
          const { $context = {} } = tooltipModel;
          const { tooltipItems = [] } = $context;
          if (tooltipItems && tooltipItems.length) {
            const dateObj = tooltipItems?.[0]?.label ? getMomentDate(tooltipItems?.[0]?.label) : '';
            const label = dateObj.format((view === 'weekly' ? DATE_FORMAT.WEEK_DATE_GRAPH : DATE_FORMAT.MONTH_DATE_GRAPH));
            const average = tooltipItems?.[1]?.raw;
            const actual = tooltipItems?.[0]?.raw;
            const innerHTML = `
              <div style="width: 150px; background-color: rgba(0, 0, 0, 0.7); border-radius: 5px; box-shadow: 0 0 5px rgba(0, 0, 0, 0.5); padding: 3px;">
                  <div style="padding: 3px; background-color: transparent; border-top-left-radius: 5px; border-top-right-radius: 5px;">
                      <span style="color: white; font-size: 15px;">${label}</span>
                  </div>
                  <hr style="margin: 0; border-color: white;">
                  <div style="padding: 3px;">
                  <p style="color: white; margin-left: 4px; font-size: 14px; display: flex; align-items: center; gap: 5px">
                  <span style="display: inline-block; width: 10px; height: 10px; border-radius: 50%; background-color: #4482FF;"></span>
                  Actual : ${actual}
                </p>
                <p style="color: white; margin-left: 4px; font-size: 14px; display: flex; align-items: center; gap: 5px">
                  <span style="display: inline-block; width: 10px; height: 10px; border-radius: 50%; background-color: #f28e00;"></span>
                  Average : ${average}
                </p>
                  </div>
              </div>`;

            tooltipEl.querySelector('table').innerHTML = innerHTML;
          }

          const position = context.chart.canvas.getBoundingClientRect();

          tooltipEl.style.opacity = '1';
          tooltipEl.style.position = 'absolute';

          const tooltipWidth = tooltipEl.offsetWidth;
          let left = position.left + window.pageXOffset + tooltipModel.caretX;
          if (left + tooltipWidth > position.right) {
            left -= (left + tooltipWidth - position.right);
          }

          tooltipEl.style.left = `${left}px`;

          tooltipEl.style.top = `${position.top + window.pageYOffset + tooltipModel.caretY}px`;
          tooltipEl.style.padding = `${tooltipModel.padding}px ${tooltipModel.padding}px`;
          tooltipEl.style.pointerEvents = 'none';
        },

      },
    },
    scales: {
      x: {
        display: false,
      },
      y: {
        ticks: { precision: 0 },
        display: false,
        suggestedMin: 0,
        suggestedMax: 100,
      },
    },
  };

  useLayoutEffect(() => {
    ChartJS.register(
      CategoryScale,
      LinearScale,
      BarElement,
      LineElement,
      PointElement,
      ArcElement,
      Title,
      Tooltip,
      Legend,
      Filler,
    );

    return () => {
      ChartJS.unregister(
        CategoryScale,
        LinearScale,
        BarElement,
        LineElement,
        PointElement,
        ArcElement,
        Title,
        Tooltip,
        Legend,
        Filler,
      );
    };
  }, [trendsData, chartData, options]);

  // const handleSetConfig = () => {
  //   navigate(
  //     '/configuration',
  //   );
  // };
  if (trendsData && trendsData.length) {
    return (
      <Box sx={{ width: 84, height: 40 }}>
        <Chart type="bar" data={chartData} options={options} />
      </Box>
    );
  }

  // return (
  //   <Typography>
  //     No Data
  //   </Typography>
  // );
}
